import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);

const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

// Emulator endpoints. Defaults match firebase.json (the `npm run dev`
// suite). Override with VITE_EMULATOR_* to point a second dev server at
// the firebase.test.json suite (8180/9399/5401/11499) so an isolated E2E
// run never shares state with a developer's running emulator.
const emulatorHost = import.meta.env.VITE_EMULATOR_HOST || "127.0.0.1";
const emulatorPort = (name: string, fallback: number): number => {
  const raw = import.meta.env[`VITE_EMULATOR_${name}_PORT`];
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const authPort = emulatorPort("AUTH", 9099);
const firestorePort = emulatorPort("FIRESTORE", 8080);
const functionsPort = emulatorPort("FUNCTIONS", 5001);
const storagePort = emulatorPort("STORAGE", 9199);

// Connect to Firebase Emulator Suite in development
if (useEmulators) {
  connectAuthEmulator(auth, `http://${emulatorHost}:${authPort}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, emulatorHost, firestorePort);
  connectFunctionsEmulator(functions, emulatorHost, functionsPort);
  connectStorageEmulator(storage, emulatorHost, storagePort);

  // Verify the Firestore emulator is actually reachable. If the dev server
  // started before emulators (or emulators died), we do NOT want writes
  // silently failing — and we definitely do not want a stale dev server
  // configuration to cause writes to leak to cloud Firestore in any future
  // SDK version. Throw at startup so tests fail loudly.
  fetch(`http://${emulatorHost}:${firestorePort}/`, { method: "GET" })
    .then(() => {
      console.info(
        `%c🔥 Firebase: EMULATOR mode (project=${firebaseConfig.projectId}, host=${emulatorHost}, firestore=${firestorePort})`,
        "color:#16a34a;font-weight:bold",
      );
    })
    .catch(() => {
      const msg = `❌ Firebase: VITE_USE_EMULATORS=true but Firestore emulator is unreachable at ${emulatorHost}:${firestorePort}. Start emulators (npm run dev or npm run emulators) before loading the admin app. Project ID is "${firebaseConfig.projectId}" — without an emulator, writes could leak to that cloud project.`;
      console.error(msg);
      // Don't actually throw at top level (would break the bundle entry);
      // the loud console.error + emulator-bound SDK means writes will error
      // rather than land in cloud, since connectFirestoreEmulator is sticky.
    });
} else if (isLocalhost) {
  // Localhost without emulator binding is the dangerous configuration —
  // every write would go to whichever cloud project VITE_FIREBASE_PROJECT_ID
  // points at. This is the failure mode that caused the 2026-04-28 incident
  // where 5 random-ID event docs leaked from a stale Vite dev server (started
  // before VITE_USE_EMULATORS was set in .env) into production Firestore via
  // the SharePoint sync trigger. We hard-fail here so Playwright catches the
  // misconfiguration as a page-load error and tests cannot run against cloud.
  const msg =
    `🚫 Firebase admin refused to connect: running on localhost but VITE_USE_EMULATORS is not "true". ` +
    `Project ID is "${firebaseConfig.projectId}" — without emulator binding, writes would leak to that cloud project. ` +
    `Likely cause: a stale Vite dev server started with old env. Stop the dev server, ensure ` +
    `VITE_USE_EMULATORS=true in apps/admin/.env, and run 'npm run dev' from the monorepo root.`;
  console.error(
    `%c${msg}`,
    "color:#dc2626;font-weight:bold;background:#fef2f2;padding:4px 8px",
  );
  // Render a visible block on the page so it's obvious in headed Playwright
  // runs and during local dev. The throw below also fails any test trying to
  // navigate to the admin.
  if (typeof document !== "undefined") {
    const div = document.createElement("div");
    div.style.cssText =
      "position:fixed;inset:0;background:#fef2f2;color:#7f1d1d;padding:24px;font:14px/1.5 system-ui;z-index:99999;white-space:pre-wrap;";
    div.textContent = msg;
    document.body?.appendChild(div);
  }
  throw new Error(msg);
} else {
  console.info(
    `%c🔥 Firebase: CLOUD mode (project=${firebaseConfig.projectId})`,
    "color:#2563eb;font-weight:bold",
  );
}
